/**
 * Action Bridge SDK — Phase 1 + Phase 2a
 *
 * Phase 1: navigate / highlight / readContext / scrollTo / openTab
 * Phase 2a: fillForm / setField / submitForm (write ops, HITL-gated)
 *
 * window.sgrcCopilot.execute(action) — allow-list enforced.
 * Selectors target data-copilot-id attributes only (never arbitrary CSS).
 */

import type { UseFormReturn } from 'react-hook-form'
import { getFormSchema } from './formSchemas'
import { flowRecorder } from './flow-recorder'

// ── Types ────────────────────────────────────────────────────────────────────

export type UiAction =
  | { op: 'navigate'; to: string; params?: Record<string, string> }
  | { op: 'highlight'; selector: string; label?: string; duration_ms?: number }
  | { op: 'readContext' }
  | { op: 'scrollTo'; selector: string }
  | { op: 'openTab'; tabId: string }
  // Phase 2a — write ops
  | {
      op: 'fillForm'
      formId: string
      values: Record<string, unknown>
      /** When true (default), emits 'copilot:preview' instead of filling immediately */
      preview?: boolean
    }
  | { op: 'setField'; formId: string; field: string; value: unknown }
  | { op: 'submitForm'; formId: string }

export interface ActionResult {
  ok: boolean
  op: string
  detail?: string
  context?: PageContext
}

export interface PageContext {
  current_route: string
  page_context: string | null
  active_form_id: string | null
}

// ── Allow-listed ops ─────────────────────────────────────────────────────────

const ALLOWED_OPS = new Set<string>([
  'navigate',
  'highlight',
  'readContext',
  'scrollTo',
  'openTab',
  // Phase 2a
  'fillForm',
  'setField',
  'submitForm',
])

// ── Injected dependencies (set by CopilotActionProvider) ─────────────────────

type NavigateFn = (to: string) => void

let _navigate: NavigateFn | null = null

export function injectNavigate(fn: NavigateFn) {
  _navigate = fn
}

// ── Form Registry (Phase 2a) ──────────────────────────────────────────────────
//
// Forms call registerForm() via useCopilotForm() on mount.
// The bridge calls methods.setValue / methods.trigger / methods.handleSubmit.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RhfMethods = UseFormReturn<any>

const _formRegistry = new Map<string, RhfMethods>()

/**
 * Register a RHF form instance so the Copilot can fill it.
 * Called by useCopilotForm on component mount.
 */
export function registerForm(formId: string, methods: RhfMethods): void {
  _formRegistry.set(formId, methods)
}

/**
 * Unregister a form instance. Called by useCopilotForm on unmount.
 */
export function unregisterForm(formId: string): void {
  _formRegistry.delete(formId)
}

/** True when a form with this id is currently mounted + registered. */
export function isFormRegistered(formId: string): boolean {
  return _formRegistry.has(formId)
}

// ── Prefill-applier registry (Phase F8 — non-RHF create surfaces) ──────────────
//
// Not every create surface is a React-Hook-Form (e.g. governance PoliciesPage,
// compliance ControlsPage/RegisterPage use plain useState dialogs). Those pages
// register a *prefill applier* via useCopilotPrefill(formId, apply) so the agentic
// smart-create flow can push confirmed values into their local state — the same
// way an RHF form gets setValue()'d. This lets "Créer avec l'IA" open + pre-fill
// ANY module's create dialog, not only the handful backed by RHF.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PrefillApplier = (values: Record<string, any>) => void

const _prefillAppliers = new Map<string, PrefillApplier>()

/** Register a non-RHF create surface's prefill applier. Returns an unsubscribe. */
export function registerPrefillApplier(formId: string, apply: PrefillApplier): () => void {
  _prefillAppliers.set(formId, apply)
  return () => {
    // Only delete if we still own the slot (guards against stale unmount races).
    if (_prefillAppliers.get(formId) === apply) _prefillAppliers.delete(formId)
  }
}

/**
 * True when a create surface for `formId` can be pre-filled — either an RHF form
 * is mounted OR a page registered a prefill applier for it.
 */
export function isCreateSurfaceReady(formId: string): boolean {
  return _formRegistry.has(formId) || _prefillAppliers.has(formId)
}

/**
 * Push values into whatever create surface owns `formId`: the RHF instance if
 * mounted, else the page-registered prefill applier. Returns true when applied.
 */
export async function applyFormValues(
  formId: string,
  values: Record<string, unknown>,
): Promise<boolean> {
  const methods = _formRegistry.get(formId)
  if (methods) {
    for (const [field, value] of Object.entries(values)) {
      methods.setValue(field, value, { shouldValidate: true, shouldDirty: true })
    }
    await methods.trigger()
    return true
  }
  const applier = _prefillAppliers.get(formId)
  if (applier) {
    applier(values)
    return true
  }
  return false
}

/**
 * Poll until the given form registers (its create dialog/page mounted) or the
 * timeout elapses. Used by the agentic "open-and-fill" flow after navigation.
 */
export function waitForForm(formId: string, timeoutMs = 5000): Promise<boolean> {
  if (_formRegistry.has(formId)) return Promise.resolve(true)
  return new Promise((resolve) => {
    const started = Date.now()
    const id = window.setInterval(() => {
      if (_formRegistry.has(formId)) {
        window.clearInterval(id)
        resolve(true)
      } else if (Date.now() - started > timeoutMs) {
        window.clearInterval(id)
        resolve(false)
      }
    }, 100)
  })
}

// ── Open-create signal (Phase F8) ─────────────────────────────────────────────
//
// The agentic "Créer avec l'IA" flow navigates to a module page and then needs
// its create dialog opened. Pages that host a create form subscribe via
// useCopilotOpenCreate(formId, open) and open their dialog when signalled.

export interface CopilotOpenCreateDetail {
  formId: string
}

declare global {
  interface WindowEventMap {
    'copilot:openCreate': CustomEvent<CopilotOpenCreateDetail>
  }
}

/** Ask the page that owns `formId` to open its create dialog/surface. */
export function requestOpenCreate(formId: string): void {
  window.dispatchEvent(
    new CustomEvent<CopilotOpenCreateDetail>('copilot:openCreate', {
      detail: { formId },
    }),
  )
}

/** Subscribe to open-create requests. Returns an unsubscribe function. */
export function onOpenCreate(handler: (formId: string) => void): () => void {
  const listener = (e: CustomEvent<CopilotOpenCreateDetail>) => handler(e.detail.formId)
  window.addEventListener('copilot:openCreate', listener as EventListener)
  return () => window.removeEventListener('copilot:openCreate', listener as EventListener)
}

// ── HighlightOverlay cleanup registry ────────────────────────────────────────

let _activeHighlightCleanup: (() => void) | null = null

function clearActiveHighlight() {
  if (_activeHighlightCleanup) {
    _activeHighlightCleanup()
    _activeHighlightCleanup = null
  }
}

// ── Core execute ─────────────────────────────────────────────────────────────

export async function executeAction(action: UiAction): Promise<ActionResult> {
  if (!ALLOWED_OPS.has(action.op)) {
    return { ok: false, op: action.op, detail: `Op "${action.op}" not in allow-list` }
  }

  // Phase 3b — Flow Recorder hook: capture state-changing/navigational ops when
  // a recording is active. Read-only ops are filtered inside recordFromBridge.
  try {
    flowRecorder.recordFromBridge(action)
  } catch {
    /* recorder must never break an action */
  }

  switch (action.op) {
    case 'navigate': {
      if (!_navigate) {
        return { ok: false, op: 'navigate', detail: 'Router not initialised' }
      }
      let target = action.to
      if (action.params) {
        const qs = new URLSearchParams(action.params).toString()
        target = `${target}?${qs}`
      }
      _navigate(target)
      return { ok: true, op: 'navigate', detail: `Navigated to ${target}` }
    }

    case 'highlight': {
      clearActiveHighlight()
      const el = resolveElement(action.selector)
      if (!el) {
        return {
          ok: false,
          op: 'highlight',
          detail: `Element [data-copilot-id="${action.selector}"] not found`,
        }
      }
      const cleanup = mountHighlightOverlay(el, action.label, action.duration_ms ?? 3000)
      _activeHighlightCleanup = cleanup
      return { ok: true, op: 'highlight', detail: `Highlighted ${action.selector}` }
    }

    case 'readContext': {
      const ctx = readPageContext()
      return { ok: true, op: 'readContext', context: ctx }
    }

    case 'scrollTo': {
      const el = resolveElement(action.selector)
      if (!el) {
        return {
          ok: false,
          op: 'scrollTo',
          detail: `Element [data-copilot-id="${action.selector}"] not found`,
        }
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return { ok: true, op: 'scrollTo', detail: `Scrolled to ${action.selector}` }
    }

    case 'openTab': {
      // Triggers a click on a tab element carrying data-copilot-id matching the tabId
      const el = resolveElement(action.tabId)
      if (!el) {
        return {
          ok: false,
          op: 'openTab',
          detail: `Tab [data-copilot-id="${action.tabId}"] not found`,
        }
      }
      ;(el as HTMLElement).click()
      return { ok: true, op: 'openTab', detail: `Opened tab ${action.tabId}` }
    }

    // ── Phase 2a ─────────────────────────────────────────────────────────

    case 'fillForm': {
      const { formId, values, preview = true } = action

      // Validate the formId is in the schema registry
      const schema = getFormSchema(formId)
      if (!schema) {
        return {
          ok: false,
          op: 'fillForm',
          detail: `Unknown formId "${formId}". Call listFormSchemas() to see available forms.`,
        }
      }

      if (preview) {
        // Emit a preview event — the HITL gate listens and shows a confirmation UI
        const formEl = document.querySelector<HTMLElement>(`[data-copilot-form="${formId}"]`)
        const target = formEl ?? document
        target.dispatchEvent(
          new CustomEvent('copilot:preview', {
            bubbles: true,
            detail: { formId, values, schema },
          })
        )
        return {
          ok: true,
          op: 'fillForm',
          detail: `Preview event emitted for form "${formId}" — awaiting HITL confirmation`,
        }
      }

      // Non-preview: fill fields immediately via RHF
      const methods = _formRegistry.get(formId)
      if (!methods) {
        return {
          ok: false,
          op: 'fillForm',
          detail: `Form "${formId}" is not mounted. Navigate to ${schema.route} first.`,
        }
      }

      const fieldNames = Object.keys(values)
      for (const field of fieldNames) {
        methods.setValue(field, values[field], { shouldValidate: true, shouldDirty: true })
      }
      // Trigger full validation after all fields are set
      await methods.trigger()

      return {
        ok: true,
        op: 'fillForm',
        detail: `Filled ${fieldNames.length} field(s) on form "${formId}": ${fieldNames.join(', ')}`,
      }
    }

    case 'setField': {
      const { formId, field, value } = action

      const schema = getFormSchema(formId)
      if (!schema) {
        return {
          ok: false,
          op: 'setField',
          detail: `Unknown formId "${formId}". Call listFormSchemas() to see available forms.`,
        }
      }

      const methods = _formRegistry.get(formId)
      if (!methods) {
        return {
          ok: false,
          op: 'setField',
          detail: `Form "${formId}" is not mounted. Navigate to ${schema.route} first.`,
        }
      }

      methods.setValue(field, value, { shouldValidate: true, shouldDirty: true })

      return {
        ok: true,
        op: 'setField',
        detail: `Set field "${field}" on form "${formId}"`,
      }
    }

    case 'submitForm': {
      const { formId } = action

      const schema = getFormSchema(formId)
      if (!schema) {
        return {
          ok: false,
          op: 'submitForm',
          detail: `Unknown formId "${formId}". Call listFormSchemas() to see available forms.`,
        }
      }

      // submitForm always routes through the HITL gate — we never submit directly.
      // Dispatch a CustomEvent on the form element (or document as fallback).
      const formEl = document.querySelector<HTMLElement>(`[data-copilot-form="${formId}"]`)
      const target = formEl ?? document
      target.dispatchEvent(
        new CustomEvent('copilot:submitRequest', {
          bubbles: true,
          detail: { formId, schema },
        })
      )

      return {
        ok: true,
        op: 'submitForm',
        detail: `Submit request dispatched for form "${formId}" — HITL gate will confirm`,
      }
    }

    default:
      return { ok: false, op: (action as UiAction).op, detail: 'Unhandled op' }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Resolve a copilot-id to its DOM element.
 * Accepts either "plain-id" (maps to data-copilot-id="plain-id") or a
 * pre-formatted selector beginning with "[" for future extension.
 */
function resolveElement(selector: string): Element | null {
  if (selector.startsWith('[')) {
    return document.querySelector(selector)
  }
  return document.querySelector(`[data-copilot-id="${selector}"]`)
}

/**
 * Mount a pulsing highlight ring around the target element via a portal <div>.
 * Returns a cleanup function that removes the overlay.
 */
function mountHighlightOverlay(
  el: Element,
  label: string | undefined,
  duration_ms: number,
): () => void {
  const rect = el.getBoundingClientRect()

  const overlay = document.createElement('div')
  overlay.setAttribute('role', 'tooltip')
  overlay.setAttribute('aria-label', label ?? 'Élément mis en évidence par le Copilot')
  overlay.style.cssText = `
    position: fixed;
    top: ${rect.top - 4 + window.scrollY}px;
    left: ${rect.left - 4}px;
    width: ${rect.width + 8}px;
    height: ${rect.height + 8}px;
    border-radius: 6px;
    pointer-events: none;
    z-index: 9999;
    animation: sgrc-copilot-pulse 1.2s ease-in-out infinite;
    outline: 3px solid hsl(var(--primary, 220 90% 56%));
    outline-offset: 2px;
    box-shadow: 0 0 0 4px hsl(var(--primary, 220 90% 56%) / 0.25);
  `

  // Inject keyframes once
  if (!document.getElementById('sgrc-copilot-kf')) {
    const style = document.createElement('style')
    style.id = 'sgrc-copilot-kf'
    style.textContent = `
      @keyframes sgrc-copilot-pulse {
        0%, 100% { box-shadow: 0 0 0 4px hsl(220 90% 56% / 0.25); }
        50%       { box-shadow: 0 0 0 8px hsl(220 90% 56% / 0.05); }
      }
    `
    document.head.appendChild(style)
  }

  // Tooltip label
  if (label) {
    const tip = document.createElement('div')
    tip.textContent = label
    tip.style.cssText = `
      position: absolute;
      top: -28px; left: 50%; transform: translateX(-50%);
      background: hsl(220 90% 56%); color: white;
      padding: 2px 8px; border-radius: 4px;
      font-size: 11px; white-space: nowrap;
      pointer-events: none;
    `
    overlay.appendChild(tip)
  }

  // Add aria-describedby to target
  const previousDescribedBy = el.getAttribute('aria-describedby')
  const descId = `sgrc-copilot-desc-${Date.now()}`
  overlay.id = descId
  el.setAttribute('aria-describedby', descId)

  document.body.appendChild(overlay)

  const timerId = window.setTimeout(() => {
    cleanup()
  }, duration_ms)

  function cleanup() {
    clearTimeout(timerId)
    overlay.remove()
    if (previousDescribedBy) {
      el.setAttribute('aria-describedby', previousDescribedBy)
    } else {
      el.removeAttribute('aria-describedby')
    }
  }

  return cleanup
}

/**
 * Read contextual metadata from the current page DOM.
 */
export function readPageContext(): PageContext {
  const contextEl = document.querySelector('[data-copilot-context]')
  const formEl = document.querySelector('[data-copilot-form]')

  return {
    current_route: window.location.pathname,
    page_context: contextEl
      ? (contextEl as HTMLElement).dataset.copilotContext ?? null
      : null,
    active_form_id: formEl
      ? (formEl as HTMLElement).dataset.copilotForm ?? null
      : null,
  }
}

// ── Global singleton exposed as window.sgrcCopilot ────────────────────────────

import { listFormSchemas, getFormSchema as _getFormSchema, type FormSchema } from './formSchemas'

declare global {
  interface Window {
    sgrcCopilot: {
      execute: (action: UiAction) => Promise<ActionResult>
      readContext: () => PageContext
      /** Phase 2a: list all forms the Copilot can fill */
      listForms: () => FormSchema[]
      /** Phase 2a: get schema for a specific formId */
      getFormSchema: (formId: string) => FormSchema | undefined
    }
  }
}

export function mountGlobalBridge() {
  window.sgrcCopilot = {
    execute: executeAction,
    readContext: readPageContext,
    listForms: listFormSchemas,
    getFormSchema: _getFormSchema,
  }
}
